import React, { useRef, useLayoutEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Button from '@material-ui/core/Button';

const isBrowser = typeof window !== `undefined`;

function getScrollPosition({ element }) {
    if (!isBrowser) return { x: 0, y: 0 };

    const target = element ? element.current : document.body;
    let { offsetWidth, scrollWidth, scrollLeft, clientHeight } = target;
    return { offsetWidth, scrollWidth, scrollLeft, clientHeight };
}

function useScrollPosition(effect, element, wait) {
    const position = useRef(getScrollPosition({}));
    const throttleTimeout = useRef(null);

    const callBack = () => {
        const currPos = getScrollPosition({ element });
        effect({ prevPos: position.current, currPos });
        position.current = currPos;
        throttleTimeout.current = null;
    };

    useLayoutEffect(() => {
        const handleScroll = () => {
            if (wait) {
                if (throttleTimeout.current === null) {
                    throttleTimeout.current = setTimeout(callBack, wait);
                }
            } else {
                callBack();
            }
        };
        const { current } = element;
        current.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);

        return () => {
            window.removeEventListener('resize', handleScroll);
            current.removeEventListener('scroll', handleScroll);
        };
    });
}

// **********************************************************************

const TableComponent = (props) => {
    const { scrollIndicator, classes, ...otherProps } = props;
    let elementRef = useRef();
    useScrollPosition(
        ({ prevPos, currPos }) => {
            if (scrollIndicator) {
                // Element scroll position
                handleScroll({ prevPos, currPos });
            }
        },
        elementRef,
        200
    );

    const handleScroll = ({ prevPos, currPos }) => {
        let { offsetWidth, scrollLeft, scrollWidth, clientHeight } = currPos;
        setClientHeight(clientHeight);
        setShowScrollIndicator(offsetWidth + scrollLeft < scrollWidth);
    };

    const scrollRight = () => {
        elementRef.current.scrollLeft += 100;
    };

    const [showScrollIndicator, setShowScrollIndicator] = React.useState(false);
    const [clientHeight, setClientHeight] = React.useState();

    const setRef = (ref) => {
        elementRef.current = ref;
        if (scrollIndicator && ref) {
            // handleScroll to show initial values of scroll posistion
            handleScroll({
                prevPos: undefined,
                currPos: getScrollPosition({ element: elementRef }),
            });
        }
    };
    return (
        <React.Fragment>
            {showScrollIndicator && (
                <Button style={{ maxHeight: clientHeight }} className={classes.scrollIndicator} size="small" onClick={scrollRight}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </Button>
            )}
            <Table classes={{ root: classes.root }} ref={(ref) => setRef(ref)} {...otherProps}></Table>
        </React.Fragment>
    );
    // return <Table ref={elementRef} {...otherProps}></Table>;
};
export const StyledTable = withStyles((theme) => ({
    root: {
        scrollBehavior: 'smooth',
        minWidth: 700,
        display: 'block',
        overflow: 'auto',
        borderTopRightRadius: theme.spacing(1),
        borderTopLeftRadius: theme.spacing(1),
        '& tbody tr:hover': {
            // backgroundColor: `${theme.palette.grey[200]} !important`,
            backgroundColor: `${theme.palette.primary.light} !important`,
        },
    },
    scrollIndicator: {
        position: 'absolute',
        zIndex: 1,
        right: '0px',
        height: '100%',
        width: '40px',
        minWidth: '40px',
        backgroundImage: `linear-gradient(to right, ${theme.palette.primary.lighter} , ${theme.palette.primary.darker})`,
        opacity: 0.8,
        // backgroundColor: theme.palette.primary.main,
    },
}))(TableComponent);

// **********************************************************************

export const StyledTableHead = withStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.primary.dark,
    },
}))(TableHead);

// **********************************************************************

const TableRowComponent = (props) => {
    const { classes, className, ...otherProps } = props;

    return <TableRow className={`${className} ${classes.tableRow}`} {...otherProps}></TableRow>;
};
export const StyledTableRow = withStyles((theme) => ({
    tableRow: {
        '&:nth-child(odd):not(.expanded)': {
            backgroundColor: theme.palette.secondary.main,
        },
        '&:nth-child(even):not(.expanded)': {
            backgroundColor: theme.palette.primary.lighter,
        },
    },
}))(TableRowComponent);

// **********************************************************************

class TableCellComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.updateSortState();
    }

    updateSortState = () => {
        let { sortOptions, field } = this.props;
        let direction = 'desc'; //default
        let active = false; //default
        if (sortOptions && field) {
            active = sortOptions[field] !== undefined;
            direction = sortOptions[field] === 1 ? 'asc' : 'desc';
        }

        this.setState({
            direction: direction,
            active: active,
        });
    };

    componentDidUpdate(prevProps) {
        if (this.props.sortOptions !== prevProps.sortOptions) {
            this.updateSortState();
        }
    }

    toggleSortDirection = () => {
        this.setState(
            (state) => {
                let direction, active;
                if (state.active === false) {
                    // toggle to active and init dierction
                    active = true;
                    direction = 'desc';
                } else {
                    // toggle either direction or back to inactive
                    if (state.direction === 'desc') {
                        active = true;
                        direction = 'asc';
                    } else {
                        active = false;
                    }
                }
                return { direction, active };
            },
            () => this.props.onSort(this.state.direction, this.state.active)
        );
    };

    renderCellContents = () => {
        if (this.props.onSort) {
            let { direction, active } = this.state;
            let { classes } = this.props;
            return (
                <TableSortLabel
                    active={active}
                    direction={direction}
                    onClick={this.toggleSortDirection}
                    hideSortIcon={true}
                    classes={{ active: classes.active, root: classes.root, icon: classes.icon }}
                >
                    {this.props.children || null}
                    {active === false && <FontAwesomeIcon icon={faSort} style={{ marginLeft: '5px' }} />}
                </TableSortLabel>
            );
        } else {
            return this.props.children || null;
        }
    };

    render() {
        // pull out onSort, sortOptions just so thaey are not passed down in otherProps
        const { fixedWidth, width, classes, onSort, sortOptions, style, ...otherProps } = this.props;
        return (
            <TableCell classes={{ head: classes.head, stickyHeader: classes.stickyHeader }} style={{ minWidth: width, width: fixedWidth, ...style }} {...otherProps}>
                {this.renderCellContents()}
            </TableCell>
        );
    }
}
export const StyledTableCell = withStyles((theme) => ({
    head: {
        color: theme.palette.common.white,
    },
    root: {
        '&:hover, &:focus': {
            color: theme.palette.common.white,
        },
    },
    active: {
        color: `${theme.palette.common.white} !important`,
        fontWeight: 'bold',
    },
    icon: {
        color: `${theme.palette.common.white} !important`,
    },
    stickyHeader: {
        backgroundColor: theme.palette.primary.dark,
    },
}))(TableCellComponent);

// **********************************************************************
